import React from "react";
import { FoodDto } from "../../api";
import { NutriValues } from "../../data/nutriValues";
import { cn } from "../../lib/utils";
import { useFoodByUnit } from "../../queries/caches";
import { Glyph, SmallIconButton } from "../SmallIconButton";
import { StepInfoBox } from "../StepInfoBox";
import { NutriRow } from "../ui/NutriRow";
import { ProductPickerButton } from "../ui/ProductPickerButton";
import { QueryBoundary } from "../ErrorBoundary";
import { LookupSkeleton } from "../ui/LookupSkeleton";

/**
 * Chip für eine Rezept-Alternative. Löst die FoodUnit per (suspending) Lookup
 * zu einem Food auf. Durch die `QueryBoundary` in `IngredientRow` werden die
 * Alternativen als Block gerendert – nie nur ein Teil (verhindert, dass bei
 * langsamem Laden einzelne Pills fehlen).
 */
export function AlternativeChip(props: {
  unitId: number;
  activeFoodId: number | undefined;
  onSelect: (food: FoodDto, unitId: number) => void;
}) {
  const food = useFoodByUnit(props.unitId);
  if (!food) return null;
  const isActive = props.activeFoodId === food.id;
  return (
    <button
      type="button"
      data-cy="alternative-chip"
      onClick={() => props.onSelect(food, props.unitId)}
      className={cn(
        "rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-all",
        isActive
          ? "border-teal bg-teal/10 text-teal"
          : "border-line bg-surface-card text-ink-muted hover:border-ink-muted hover:text-ink",
      )}
    >
      {food.description}
    </button>
  );
}

export interface IngredientRowProps {
  title: string;
  status?: string;
  alternativeUnitIds?: number[];
  activeFoodId?: number;
  onSelectAlternative?: (food: FoodDto, unitId: number) => void;
  stepInfo?: { index: number; text?: string | null };
  /** Die Mengen-Steuerung (`UnitAmount`/`PurchaseUnitAmount`) des Aufrufers. */
  amount: React.ReactNode;
  onDelete?: () => void;
  deleteLabel?: string;
  /** Zusätzliche Aktion neben dem Löschen (z. B. "Aufteilen"). */
  trailing?: React.ReactNode;
  /** Zusatzinhalt direkt unter der Mengen-Zeile (z. B. Personen-Aufteilung). */
  extra?: React.ReactNode;
  nutri?: NutriValues;
  product?: {
    onClick: () => void;
    highlighted?: boolean;
    dataCy?: string;
  } | null;
  isLast?: boolean;
  className?: string;
  dataCy?: string;
}

/**
 * Einheitliche Zutaten-Zeile für Planen, Verzehr und Kochdialog: Titel,
 * Alternativen-Pillen, optionaler Schritt-Hinweis, Menge + Löschen und die
 * Nährwerte der Zutat – als flache Zeile mit Trenner statt eigener Card.
 */
export function IngredientRow(props: IngredientRowProps) {
  const alternatives = props.alternativeUnitIds ?? [];
  const showAlternatives = alternatives.length > 1 && !!props.onSelectAlternative;

  return (
    <div
      data-cy={props.dataCy}
      className={cn(
        "flex flex-col gap-2 px-3 py-3",
        !props.isLast && "border-b border-line/40",
        props.className,
      )}
    >
      <span className="text-sm leading-snug text-ink">{props.title}</span>

      {props.status && (
        <span className="text-xs font-semibold text-teal">{props.status}</span>
      )}

      {showAlternatives && (
        <QueryBoundary
          fallback={
            <LookupSkeleton
              dataCy="alternative-loading"
              className="h-5 w-32"
            />
          }
        >
          <div className="flex flex-wrap gap-1.5">
            {alternatives.map((unitId) => (
              <AlternativeChip
                key={unitId}
                unitId={unitId}
                activeFoodId={props.activeFoodId}
                onSelect={props.onSelectAlternative!}
              />
            ))}
          </div>
        </QueryBoundary>
      )}

      {props.stepInfo && (
        <StepInfoBox index={props.stepInfo.index} text={props.stepInfo.text} />
      )}

      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">{props.amount}</div>
        {props.trailing}
        {props.onDelete && (
          <SmallIconButton
            label={props.deleteLabel ?? "Zutat entfernen"}
            icon="delete"
            variant="destructive"
            onClick={props.onDelete}
          />
        )}
      </div>

      {props.extra}

      {props.nutri && <NutriRow nutri={props.nutri} />}

      {props.product && (
        <ProductPickerButton
          dataCy={props.product.dataCy}
          highlighted={props.product.highlighted}
          onClick={props.product.onClick}
        />
      )}
    </div>
  );
}

/** Die "+ X hinzufügen"-Zeile für eine im Verzehr entfernte Zutat. */
export function IngredientRestoreRow(props: {
  name: string;
  onRestore: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onRestore}
      className="flex h-9 w-full items-center justify-start gap-2 border-t border-line/40 px-3 text-xs text-ink-muted transition hover:bg-surface hover:text-ink active:opacity-80"
    >
      <Glyph icon="plus" size={14} />
      <span>{props.name} hinzufügen</span>
    </button>
  );
}
